import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "./Dashboard";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, User, ShieldCheck, MailCheck, Globe, Database, RefreshCw, Sparkles, MonitorSmartphone, Trash2 } from "lucide-react";
import LanguageSwitcher from "../../components/LanguageSwitcher";

export default function Settings({ embedded = false }) {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();

  // ----- name (instant, no OTP) — shared with My personal details -----
  const [name, setName] = useState("");
  useEffect(() => { setName(user?.name || ""); }, [user]);
  const [nameBusy, setNameBusy] = useState(false);
  const nameDirty = (name || "").trim() !== (user?.name || "").trim();

  // ----- phone / email (OTP-gated) -----
  const [form, setForm] = useState({ phone: "", email: "" });
  useEffect(() => {
    if (user) setForm({
      phone: user.phone || "",
      email: user.email || "",
    });
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirtyFields = Object.entries(form).filter(([k, v]) => (v || "") !== (user?.[k] || ""));
  const isDirty = dirtyFields.length > 0;

  const [otpOpen, setOtpOpen] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [otp, setOtp] = useState("");
  const [pendingFields, setPendingFields] = useState([]);
  const [otpSentTo, setOtpSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const requestChange = async (e) => {
    e?.preventDefault?.();
    setErr(null); setBusy(true);
    try {
      const payload = {};
      dirtyFields.forEach(([k, v]) => { payload[k] = v?.trim() || null; });
      const { data } = await api.post("/profile/request-change", payload);
      setRequestId(data.request_id);
      setPendingFields(data.fields);
      setOtpSentTo(data.otp_sent_to || user?.email || "");
      setOtp(""); setOtpOpen(true);
      toast.success(`Verification code sent to ${data.otp_sent_to || "your email"}`);
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally { setBusy(false); }
  };

  const confirmChange = async () => {
    setErr(null); setBusy(true);
    try {
      const { data } = await api.post("/profile/confirm-change", { request_id: requestId, otp: otp.trim() });
      setUser(data.user);
      setOtpOpen(false);
      setOtp(""); setRequestId(null);
      toast.success("Profile updated");
      if (data.email_changed) {
        toast.message("Your sign-in email has been updated and verified.");
      }
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally { setBusy(false); }
  };

  const saveName = async () => {
    setNameBusy(true);
    try {
      const { data } = await api.patch("/profile/name", { name: name.trim() });
      setUser(data.user);
      toast.success("Name updated");
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally { setNameBusy(false); }
  };

  const cancelChange = async () => {
    try { await api.post("/profile/cancel-change"); } catch {}
    setOtpOpen(false); setRequestId(null); setOtp("");
  };

  // ----- change password -----
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdErr, setPwdErr] = useState(null);

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdErr(null);
    if (nw.length < 6) return setPwdErr("New password must be at least 6 characters");
    if (nw !== nw2) return setPwdErr("Passwords don't match");
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw(""); setNw2("");
    } catch (e2) {
      setPwdErr(fmtErr(e2));
    } finally { setPwdBusy(false); }
  };

  return (
    <div className={embedded ? "" : "p-6 lg:p-10 max-w-3xl"}>
      {!embedded && <PageHeader overline="Account" title={t("settings.title")} />}

      {/* Language section — at top so it's the first thing crew finds when
          they want to switch the UI to their own language. Backend-synced. */}
      <div className="mt-8 rounded-md border border-zinc-200 bg-white p-6" data-testid="settings-language">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-4 w-4 text-zinc-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("settings.language_section_title")}</div>
        </div>
        <div className="text-sm text-zinc-600 mb-4">{t("settings.language_section_subtitle")}</div>
        <LanguageSwitcher variant="full" testid="settings-lang-switcher" />
      </div>

      {/* Profile — Full name applies instantly; phone & email are OTP-gated.
          These three fields are shared with "My personal details". */}
      <div className="mt-8 rounded-md border border-zinc-200 bg-white p-6" data-testid="profile-form">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-4 w-4 text-zinc-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Profile</div>
        </div>

        {/* Profile photo — shown to yacht owners as their contact person's face */}
        <div className="flex items-center gap-4 mb-6" data-testid="profile-photo-block">
          {user?.picture
            ? <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover border border-zinc-200" data-testid="profile-photo-img" />
            : <div className="h-16 w-16 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 text-lg font-medium">
                {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
              </div>}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <input
                id="avatar-file" type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  try {
                    const { data } = await api.post("/auth/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setUser((u) => ({ ...u, picture: data.picture }));
                    toast.success("Photo updated");
                  } catch (err) { toast.error(fmtErr(err)); }
                }}
              />
              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("avatar-file").click()} data-testid="profile-photo-upload">
                Upload photo
              </Button>
              {user?.picture && (
                <Button type="button" variant="ghost" size="sm" className="text-zinc-500"
                  onClick={async () => {
                    try { await api.delete("/auth/me/photo"); setUser((u) => ({ ...u, picture: null })); toast.success("Photo removed"); }
                    catch (err) { toast.error(fmtErr(err)); }
                  }} data-testid="profile-photo-remove">
                  Remove
                </Button>
              )}
            </div>
            <p className="text-[11px] text-zinc-400">JPG, PNG or WebP, max 5 MB. Yacht clients see it next to your name.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <Label htmlFor="p-name">Account Name</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
          </div>
          <div>
            <Button type="button" onClick={saveName} disabled={!nameDirty || nameBusy}
              className="bg-ink hover:bg-black" data-testid="profile-name-save">
              {nameBusy ? "Saving…" : "Save name"}
            </Button>
          </div>
        </div>

        <form onSubmit={requestChange} className="mt-6 border-t border-zinc-100 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p-phone">Phone</Label>
              <Input id="p-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" />
            </div>
            <div>
              <Label htmlFor="p-email" className="flex items-center gap-2">
                Email
                {user?.email_verified && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" /> verified
                  </span>
                )}
              </Label>
              <Input id="p-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="profile-email" />
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="text-xs text-zinc-500 flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" />
              {isDirty
                ? "Phone changes are confirmed by a code sent to your current email; email changes by a code sent to the new address."
                : "Edit phone or email, then save to receive a verification code."}
            </div>
            <Button
              type="submit"
              disabled={!isDirty || busy}
              className="bg-ink hover:bg-black"
              data-testid="profile-save"
            >
              {busy ? "Sending…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>

      {/* Account meta */}
      <div className="mt-6 rounded-md border border-zinc-200 bg-white p-6 space-y-3">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Account</div>
        <Row k="Sign-in method" v={user?.auth_provider} />
        <Row k="User ID" v={<span className="font-mono text-xs">{user?.id}</span>} />
      </div>

      {/* Storage & usage — admin only; silently hidden for everyone else. */}
      <StorageUsageCard />

      <DevicesCard />

      {/* Change password */}
      <form onSubmit={changePassword} className="mt-6 rounded-md border border-zinc-200 bg-white p-6" data-testid="change-pwd-form">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-4 w-4 text-zinc-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Change password</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="cur">Current password</Label>
            <Input id="cur" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="pwd-current" />
          </div>
          <div>
            <Label htmlFor="nw">New password</Label>
            <Input id="nw" type="password" value={nw} onChange={(e) => setNw(e.target.value)} required data-testid="pwd-new" />
          </div>
          <div>
            <Label htmlFor="nw2">Confirm new password</Label>
            <Input id="nw2" type="password" value={nw2} onChange={(e) => setNw2(e.target.value)} required data-testid="pwd-new2" />
          </div>
          {pwdErr && <div className="md:col-span-3 text-sm text-red-600" data-testid="pwd-error">{pwdErr}</div>}
          <div className="md:col-span-3">
            <Button type="submit" disabled={pwdBusy} className="bg-ink hover:bg-black" data-testid="pwd-save">
              {pwdBusy ? "Updating…" : "Update password"}
            </Button>
          </div>
        </div>
      </form>

      <div className="mt-6">
        <Button variant="outline" onClick={async () => { await logout(); nav("/"); }} data-testid="settings-logout">
          Sign out
        </Button>
      </div>

      {/* Danger zone — self-delete account */}
      <DangerZone />

      {/* OTP confirmation dialog */}
      <Dialog open={otpOpen} onOpenChange={(o) => { if (!o) cancelChange(); }}>
        <DialogContent data-testid="profile-otp-dialog">
          <DialogHeader>
            <DialogTitle>Confirm profile changes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3">
              <MailCheck className="h-5 w-5 text-zinc-600 shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-700">
                We sent a 6-digit code to <span className="font-medium">{otpSentTo || user?.email}</span>. Enter it to apply
                changes to: <span className="font-medium">{pendingFields.join(", ")}</span>.
              </div>
            </div>
            <div>
              <Label htmlFor="otp">Verification code</Label>
              <Input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                className="font-mono tracking-[0.4em] text-center text-lg"
                data-testid="profile-otp-input"
              />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="profile-otp-error">{err}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelChange} disabled={busy}>Cancel</Button>
            <Button onClick={confirmChange} disabled={busy || otp.length !== 6} className="bg-ink hover:bg-black" data-testid="profile-otp-confirm">
              {busy ? "Confirming…" : "Confirm changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 items-center border-b border-zinc-100 last:border-0 pb-2 last:pb-0">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{k}</div>
      <div className="col-span-2 text-sm">{v}</div>
    </div>
  );
}

/* ─── Storage & Usage (admin only) ───────────────────────────────────────
 * Reads live MongoDB size + per-collection counts + uploaded-file totals
 * from GET /api/admin/storage-stats. The endpoint is admin-gated, so this
 * card silently renders nothing for non-admin users (403 → hidden). On the
 * production deploy it reports the production database. */
function DevicesCard() {
  const [devices, setDevices] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/auth/devices");
      setDevices(data || []);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    try {
      await api.delete(`/auth/devices/${id}`);
      toast.success("Device removed. A future sign-in from it will count as a new device.");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  if (!devices.length) return null;
  const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");
  return (
    <div className="mt-6 rounded-md border border-zinc-200 bg-white p-6" data-testid="devices-card">
      <div className="flex items-center gap-2 mb-1">
        <MonitorSmartphone className="h-4 w-4 text-zinc-500" />
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Known devices</div>
      </div>
      <div className="text-sm text-zinc-600 mb-4">
        Devices that have signed in to your account. Yacht owners get a security email when a new
        device signs in. Remove devices you no longer use.
      </div>
      <div className="divide-y divide-zinc-100">
        {devices.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`device-${d.id}`}>
            <div className="min-w-0 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">{d.label}</span>
                {d.current && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium" data-testid={`device-current-${d.id}`}>
                    This device
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 truncate">
                IP {d.ip || "unknown"} · last seen {fmtDate(d.last_seen)}
              </div>
            </div>
            {!d.current && (
              <button
                type="button"
                className="text-red-500 hover:text-red-600 shrink-0"
                onClick={() => remove(d.id)}
                data-testid={`device-remove-${d.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StorageUsageCard() {
  const [stats, setStats] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [demo, setDemo] = useState(null);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo");
      setDemo(data);
      toast.success("Demo data seeded. Share the credentials below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not seed demo data.");
    } finally {
      setSeeding(false);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/storage-stats");
      setStats(data);
    } catch (e) {
      // 403 (not admin) or any error → hide the card entirely.
      setHidden(true);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (hidden) return null;

  return (
    <div className="mt-6 rounded-md border border-zinc-200 bg-white p-6" data-testid="storage-usage-card">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-zinc-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Storage &amp; usage</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={busy}
          className="h-7 px-2 text-zinc-500"
          data-testid="storage-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {!stats ? (
        <div className="text-sm text-zinc-500">{busy ? "Loading…" : "No data."}</div>
      ) : (
        <>
          <div className="text-xs text-zinc-500 mb-3">
            Database <span className="font-mono">{stats.db_name}</span> · {stats.objects.toLocaleString()} documents across {stats.collection_count} collections
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Stat label="Data" value={`${stats.data_mb} MB`} testid="storage-data" />
            <Stat label="Indexes" value={`${stats.index_mb} MB`} testid="storage-index" />
            <Stat label="Total (DB)" value={`${stats.total_mb} MB`} testid="storage-total" emphasis />
            <Stat label="Files" value={`${stats.files.storage_mb} MB`} sub={`${stats.files.count.toLocaleString()} files`} testid="storage-files" />
          </div>

          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Largest collections</div>
          <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="storage-collections">
            {stats.collections.filter((c) => c.count > 0).slice(0, 12).map((c) => (
              <li key={c.name} className="flex items-center justify-between gap-3 text-sm border-b border-zinc-100 last:border-0 pb-1.5">
                <span className="font-mono text-xs text-zinc-700 truncate">{c.name}</span>
                <span className="flex items-center gap-3 shrink-0 text-zinc-500">
                  <span>{c.count.toLocaleString()} docs</span>
                  <span className="tabular-nums w-16 text-right">{c.data_mb} MB</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-start gap-2 text-xs text-zinc-500">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Visible to the admin account only. Numbers reflect the database this app is connected to (production when deployed on seavanto.io).
          </div>
        </>
      )}

      {/* Demo data — admin only (this whole card is admin-gated). One click
          creates/refreshes the shareable read-only demo account + demo yacht
          with sample data. Safe & idempotent: never touches real yachts. */}
      <div className="mt-5 border-t border-zinc-200 pt-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-zinc-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Demo data</div>
        </div>
        <p className="text-xs text-zinc-500 mb-3">
          Create or refresh the shareable <strong>read-only demo</strong> (demo yacht + sample data). Run this once after each deploy on this environment. It only ever touches the demo yacht — your real data is never modified.
        </p>
        <Button onClick={seedDemo} disabled={seeding} className="bg-ink hover:bg-black" data-testid="seed-demo-btn">
          <Sparkles className="h-4 w-4 mr-2" />{seeding ? "Seeding…" : "Seed / refresh demo data"}
        </Button>
        {demo && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="demo-creds">
            <div className="font-medium text-emerald-800 mb-1">Demo account ready — share these:</div>
            <div className="font-mono text-[13px] text-emerald-900">{demo.demo_email}</div>
            <div className="font-mono text-[13px] text-emerald-900">{demo.demo_password}</div>
            <div className="text-[11px] text-emerald-700 mt-1">Role: viewer (read-only). {demo.note}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, testid, emphasis }) {
  return (
    <div className={`rounded-md border p-3 ${emphasis ? "border-zinc-300 bg-zinc-50" : "border-zinc-200"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="text-lg font-semibold text-zinc-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}




const BUSINESS_FIELDS = [
  { key: "account_holder", label: "Account holder", placeholder: "Full legal name", group: "bank" },
  { key: "bank_name", label: "Bank name", placeholder: "Name of bank", group: "bank" },
  { key: "iban", label: "IBAN", placeholder: "International Bank Account Number", group: "bank", mono: true },
  { key: "bic_swift", label: "BIC / SWIFT", placeholder: "Bank identifier code", group: "bank", mono: true },
  { key: "vat_id", label: "VAT ID", placeholder: "Tax / VAT registration number", group: "tax" },
  { key: "tax_id", label: "Tax ID", placeholder: "(optional)", group: "tax" },
  { key: "website", label: "Website", placeholder: "https://example.com", group: "addr" },
  { key: "address_line1", label: "Address line 1", placeholder: "Street and number", group: "addr" },
  { key: "address_line2", label: "Address line 2", placeholder: "Apt, suite, floor (optional)", group: "addr" },
  { key: "city", label: "City", placeholder: "City", group: "addr" },
  { key: "postcode", label: "Postcode", placeholder: "Postal / ZIP code", group: "addr" },
  { key: "country", label: "Country", placeholder: "Country", group: "addr" },
];

export function BusinessDetailsCard() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Lazy-load on first expand
  useEffect(() => {
    let alive = true;
    if (!open || data) return;
    (async () => {
      try {
        const { data: d } = await api.get("/auth/business-details");
        if (!alive) return;
        setData(d);
        setDraft(d);
      } catch (e) {
        toast.error(fmtErr(e));
      }
    })();
    return () => { alive = false; };
  }, [open, data]);

  const set = (k, v) => {
    setDraft((cur) => ({ ...cur, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.patch("/auth/business-details", draft);
      setData(d);
      setDraft(d);
      setDirty(false);
      toast.success("Business details saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const filled = data
    ? BUSINESS_FIELDS.filter((f) => (data[f.key] || "").toString().trim()).length
    : 0;

  return (
    <div className="mt-8 rounded-md border border-zinc-200 bg-white" data-testid="business-details-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-5 flex items-center justify-between gap-3 hover:bg-zinc-50/60"
        data-testid="business-details-toggle"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Business details</div>
          <div className="text-sm font-medium mt-0.5">VAT, banking & address</div>
          <div className="text-[11px] text-zinc-500 mt-1 max-w-md">
            Optional. Useful for invoicing — yacht owners you work with will
            see these on your supplier card. Edit any time.
          </div>
        </div>
        <span className="text-xs text-zinc-500 shrink-0">
          {data ? `${filled}/${BUSINESS_FIELDS.length} filled` : ""}
          <span className="ml-2">{open ? "▲" : "▼"}</span>
        </span>
      </button>

      {open && data && (
        <div className="border-t border-zinc-100 p-5 space-y-5" data-testid="business-details-form">
          <Section title="Company / personal address">
            <Grid>
              {BUSINESS_FIELDS.filter((f) => f.group === "addr").map((f) => (
                <Field key={f.key} f={f} value={draft[f.key] || ""} onChange={(v) => set(f.key, v)} />
              ))}
            </Grid>
          </Section>
          <Section title="Tax">
            <Grid>
              {BUSINESS_FIELDS.filter((f) => f.group === "tax").map((f) => (
                <Field key={f.key} f={f} value={draft[f.key] || ""} onChange={(v) => set(f.key, v)} />
              ))}
            </Grid>
          </Section>
          <Section title="Banking" subtitle="Stored as you type (no field-level encryption). Visible to seniors on yachts you serve.">
            <Grid>
              {BUSINESS_FIELDS.filter((f) => f.group === "bank").map((f) => (
                <Field key={f.key} f={f} value={draft[f.key] || ""} onChange={(v) => set(f.key, v)} />
              ))}
            </Grid>
          </Section>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { setDraft(data); setDirty(false); }}
              disabled={busy || !dirty}
              data-testid="business-details-reset"
            >Reset</Button>
            <Button
              onClick={save}
              disabled={busy || !dirty}
              className="bg-ink hover:bg-black"
              data-testid="business-details-save"
            >{busy ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{title}</div>
      {subtitle && <div className="text-[11px] text-zinc-400 mt-0.5">{subtitle}</div>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ f, value, onChange }) {
  return (
    <div>
      <Label htmlFor={`biz-${f.key}`}>{f.label}</Label>
      <Input
        id={`biz-${f.key}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={f.placeholder}
        className={f.mono ? "font-mono text-sm" : ""}
        data-testid={`biz-${f.key}`}
      />
    </div>
  );
}



function DangerZone() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("idle");  // 'idle' | 'otp'
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState(null);

  const request = async () => {
    setErr(null); setBusy(true);
    try {
      await api.post("/auth/request-account-delete");
      setStep("otp");
      toast.success("Code sent — check your email");
    } catch (e) {
      const msg = fmtErr(e);
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setErr(null); setBusy(true);
    try {
      await api.post("/auth/confirm-account-delete", { otp });
      toast.success("Account deleted");
      await logout();
      nav("/", { replace: true });
    } catch (e) {
      setErr(fmtErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-12 border border-red-200 bg-red-50/30 rounded-lg p-6" data-testid="danger-zone">
      <h2 className="font-display text-lg text-red-700">Delete my account</h2>
      <p className="text-sm text-zinc-600 mt-1 max-w-xl">
        Permanent — your profile and address book are removed. You can only
        delete your account if you're not on any yacht crew and don't own any
        yacht. Leave or transfer every yacht first.
      </p>

      {step === "idle" && (
        <Button
          variant="outline"
          onClick={request}
          disabled={busy}
          className="mt-4 border-red-300 text-red-700 hover:bg-red-100"
          data-testid="request-account-delete"
        >
          {busy ? "Sending code…" : "Send verification code"}
        </Button>
      )}

      {step === "otp" && (
        <div className="mt-4 space-y-3 max-w-md">
          <Label htmlFor="del-otp">6-digit code from your email</Label>
          <Input
            id="del-otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            className="font-mono tracking-[0.4em] text-center text-lg"
            placeholder="000000"
            data-testid="account-delete-otp"
          />
          {err && <div className="text-sm text-red-600">{err}</div>}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setStep("idle"); setOtp(""); setErr(null); }}
              disabled={busy}
              data-testid="account-delete-cancel"
            >Cancel</Button>
            <Button
              onClick={confirm}
              disabled={busy || otp.length !== 6}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="account-delete-confirm"
            >{busy ? "Deleting…" : "Delete account permanently"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
